﻿<script setup>
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRecentlyViewedStore } from '@/stores/recentlyViewed'
import { useTransactionStore } from '@/stores/transaction'

/**
 * RED-236 — "Viimati vaadatud" panel, anchored bottom-left on the Tehingute
 * kaart. Compact single-line rows (Siim comment 10b: smaller, one object per
 * row, reduced height) so it doesn't cover the map. Hidden entirely when the
 * list is empty ("kõik nii nagu enne"). Row click re-opens the transaction —
 * the parent pans the map + opens the right sidebar; the map draws the halo.
 */

const { t } = useI18n()

const recentlyViewedStore = useRecentlyViewedStore()
const { items, open, count } = storeToRefs(recentlyViewedStore)

const transactionStore = useTransactionStore()
const { selectedTransaction } = storeToRefs(transactionStore)
const markedIds = computed(() => transactionStore.getMarkedTransactions)

const legendOpen = ref(false)

const emit = defineEmits(['select'])

const MARKER_HEX = {
  default: '#027BFA',
  ad_link: '#2DA74E',
  ad_link_not_found: '#FFC107',
  not_vabaturu_tehing: '#FF4081',
  comparison: '#DB3848',
}

// Dot colour mirrors the map marker vocabulary + comparison priority.
const markerColor = item => {
  if (markedIds.value.includes(item.id)) return MARKER_HEX.comparison
  if (item.not_vabaturu_tehing) return MARKER_HEX.not_vabaturu_tehing
  if (item.ad_link) return MARKER_HEX.ad_link
  if (item.ad_link_not_found) return MARKER_HEX.ad_link_not_found
  return MARKER_HEX.default
}

const formatPrice = value => {
  if (value == null || value === '') return ''
  const n = Number(value)
  if (Number.isNaN(n)) return String(value)
  return n.toLocaleString('et-EE').replace(/\s/g, ' ') + ' €'
}

// API dates are already dd.MM.yyyy; normalise an ISO value defensively.
const formatDate = value => {
  if (!value) return ''
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  return iso ? `${iso[3]}.${iso[2]}.${iso[1]}` : value
}

const legendRows = computed(() => [
  { color: MARKER_HEX.default, label: t('messages.transactions.recently_viewed.legend.default') },
  { color: MARKER_HEX.ad_link, label: t('messages.transactions.recently_viewed.legend.ad_link') },
  { color: MARKER_HEX.ad_link_not_found, label: t('messages.transactions.recently_viewed.legend.ad_link_not_found') },
  { color: MARKER_HEX.not_vabaturu_tehing, label: t('messages.transactions.recently_viewed.legend.not_vabaturu') },
  { color: MARKER_HEX.comparison, label: t('messages.transactions.recently_viewed.legend.comparison') },
])

const onRowClick = item => {
  emit('select', item)
}

const onRemove = id => {
  recentlyViewedStore.remove(id)
}

const onClear = () => {
  recentlyViewedStore.clear()
}
</script>

<template>
  <div
    v-if="count > 0"
    class="fixed bottom-3 left-3 z-30 flex flex-col items-start gap-2"
    data-testid="recently-viewed"
  >
    <!-- Expanded card (renders above the dock toggle) -->
    <div
      v-if="open"
      class="w-72 max-h-[52vh] flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800"
      data-testid="recently-viewed-card"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between gap-2 border-b border-gray-200 px-3 py-2 dark:border-gray-700"
      >
        <span class="flex min-w-0 items-center gap-1.5">
          <i class="pi pi-history text-sm text-gray-400"></i>
          <span class="truncate text-sm font-semibold text-gray-700 dark:text-gray-200">
            {{ $t('messages.transactions.recently_viewed.title') }}
          </span>
        </span>
        <button
          type="button"
          class="shrink-0 rounded-sm px-1.5 py-0.5 text-xs font-semibold text-primary-600 hover:text-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/40"
          data-testid="recently-viewed-clear"
          @click="onClear"
        >
          {{ $t('messages.transactions.recently_viewed.clear') }}
        </button>
      </div>

      <!-- Rows (single line each, compact) -->
      <div class="flex-1 overflow-y-auto recently-viewed-scroll">
        <div
          v-for="item in items"
          :key="item.id"
          class="group flex items-center border-b border-gray-100 last:border-b-0 dark:border-gray-700/60"
          :class="
            selectedTransaction && selectedTransaction.id === item.id
              ? 'bg-primary-50 dark:bg-primary-900/30'
              : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
          "
        >
          <button
            type="button"
            class="flex min-w-0 flex-1 items-center gap-2 px-2.5 py-1.5 text-left"
            data-testid="recently-viewed-row"
            @click="onRowClick(item)"
          >
            <span
              class="h-2.5 w-2.5 shrink-0 rounded-full"
              :style="{ backgroundColor: markerColor(item) }"
            ></span>
            <span
              class="min-w-0 flex-1 truncate text-xs font-medium text-gray-700 dark:text-gray-200"
            >
              {{ item.aadress }}
            </span>
            <span class="shrink-0 text-[11px] tabular-nums text-gray-400 dark:text-gray-500">
              {{ formatDate(item.kpv) }}
            </span>
            <span
              class="shrink-0 text-[11px] font-semibold tabular-nums text-gray-600 dark:text-gray-300"
            >
              {{ formatPrice(item.tehingusumma) }}
            </span>
          </button>
          <button
            type="button"
            class="mr-1 shrink-0 rounded-sm p-1 text-gray-300 opacity-70 hover:bg-gray-200 hover:text-gray-600 group-hover:opacity-100 dark:hover:bg-gray-600"
            :aria-label="$t('messages.transactions.recently_viewed.remove')"
            :title="$t('messages.transactions.recently_viewed.remove')"
            data-testid="recently-viewed-remove"
            @click.stop="onRemove(item.id)"
          >
            <i class="pi pi-times text-[10px]"></i>
          </button>
        </div>
      </div>

      <!-- Legend (collapsible) -->
      <div class="border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          class="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300"
          data-testid="recently-viewed-legend-toggle"
          @click="legendOpen = !legendOpen"
        >
          <span class="flex items-center gap-1.5">
            <i class="pi pi-info-circle text-xs text-gray-400"></i>
            {{ $t('messages.transactions.recently_viewed.legend.title') }}
          </span>
          <i
            class="pi text-[10px] text-gray-400"
            :class="legendOpen ? 'pi-chevron-up' : 'pi-chevron-down'"
          ></i>
        </button>
        <div v-if="legendOpen" class="px-3 pb-2.5">
          <div class="flex flex-wrap gap-x-3 gap-y-1">
            <span
              v-for="row in legendRows"
              :key="row.label"
              class="inline-flex items-center gap-1.5 text-[11px] text-gray-600 dark:text-gray-300"
            >
              <span class="h-2.5 w-2.5 rounded-xs" :style="{ backgroundColor: row.color }"></span>
              {{ row.label }}
            </span>
          </div>
          <div
            class="mt-2 flex flex-col gap-1 border-t border-gray-100 pt-2 text-[11px] text-gray-500 dark:border-gray-700 dark:text-gray-400"
          >
            <span>{{ $t('messages.transactions.recently_viewed.legend.viewed') }}</span>
            <span>{{ $t('messages.transactions.recently_viewed.legend.open') }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Dock toggle button. Typography + radius match the PrimeVue small Button
         used by the "Võrreldavate tehingute tabel" broker button (rounded-md /
         font-medium / text-sm) so the two map controls read as one design
         language — only the colour differs (RED-236 c). -->
    <button
      type="button"
      class="inline-flex h-10 items-center gap-2 rounded-md border px-3 text-sm font-medium shadow-md transition-colors"
      :class="
        open
          ? 'border-primary-600 bg-primary-500 text-white'
          : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200'
      "
      :aria-pressed="open"
      data-testid="recently-viewed-toggle"
      @click="open = !open"
    >
      <i class="pi pi-history text-sm"></i>
      <span>{{ $t('messages.transactions.recently_viewed.title') }}</span>
      <span
        class="inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-bold tabular-nums text-white"
        :class="open ? 'bg-white/25' : 'bg-primary-500'"
      >
        {{ count }}
      </span>
      <i class="pi text-[10px] opacity-80" :class="open ? 'pi-chevron-down' : 'pi-chevron-up'"></i>
    </button>
  </div>
</template>

<style scoped>
  @reference '../../../css/tailwind.css';

.recently-viewed-scroll {
  scrollbar-width: thin;
  scrollbar-color: rgb(203 213 225) transparent;
}
.recently-viewed-scroll::-webkit-scrollbar {
  width: 8px;
}
.recently-viewed-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.recently-viewed-scroll::-webkit-scrollbar-thumb {
  @apply bg-gray-300 dark:bg-gray-600;
  border-radius: 4px;
  border: 2px solid transparent;
  background-clip: content-box;
}
</style>
